import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { Plug, Wifi, WifiOff, Monitor, RefreshCw, Check, AlertCircle, Trash2, Link, Unlink } from 'lucide-react';

function TvPower() {
  const { currentUser } = useAuth();

  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [wyzeEmail, setWyzeEmail] = useState('');
  const [wyzePassword, setWyzePassword] = useState('');
  const [wyzeKeyId, setWyzeKeyId] = useState('');
  const [wyzeApiKey, setWyzeApiKey] = useState('');

  // Plugs state
  const [plugs, setPlugs] = useState([]);
  const [plugStates, setPlugStates] = useState({});
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState('');
  const [togglingMac, setTogglingMac] = useState(null);
  // Screens state
  const [screens, setScreens] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Loading
  const [loading, setLoading] = useState(true);

  // Load existing config + screens on mount
  const loadData = useCallback(async () => {
    if (!currentUser?.uid) return;

    try {
      // Load TV Power config
      const configRef = doc(db, 'users', currentUser.uid, 'tvPower', 'config');
      const configSnap = await getDoc(configRef);

      if (configSnap.exists()) {
        const config = configSnap.data();
        setIsConnected(config.isConnected || false);
        setPlugs(config.plugs || []);
        setAssignments(config.assignments || {});
        setPlugStates(config.plugStates || {});
        if (config.wyzeEmailDisplay) setWyzeEmail(config.wyzeEmailDisplay);
      }

  // Load user's screens
      const screensSnapshot = await getDocs(collection(db, 'screens'));
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};
      const isAdmin = userData.role === 'super_admin';

      const screensData = screensSnapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(screen => {
          if (screen.screenStatus === 'inactive') return false;
          if (isAdmin) {
            return !screen.userId;
          } else {
            return screen.userId === currentUser.uid;
          }
        });

      screensData.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
        return (a.screenName || '').localeCompare(b.screenName || '');
      });

      setScreens(screensData);
    } catch (error) {
      console.error('Error loading TV Power config:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Step 1: Connect Wyze account
  const handleConnect = async () => {
    if (!wyzeEmail.trim() || !wyzePassword.trim()) {
      setConnectionError('Email and password are required.');
      return;
    }
    if (!wyzeKeyId.trim() || !wyzeApiKey.trim()) {
      setConnectionError('Wyze API Key ID and API Key are required.');
      return;
    }

    setIsConnecting(true);
    setConnectionError('');

    try {
      // Call Firebase Cloud Function to authenticate with Wyze
      const response = await fetch(
        `${process.env.REACT_APP_CLOUD_FUNCTIONS_URL}/wyzeConnect`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: currentUser.uid,
            email: wyzeEmail.trim(),
            password: wyzePassword.trim(),
            keyId: wyzeKeyId.trim(),
            apiKey: wyzeApiKey.trim()
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to connect to Wyze.');
      }


      setIsConnected(true);
      setWyzePassword('');
    } catch (error) {
      console.error('Wyze connect error:', error);
      setConnectionError(error.message || 'Connection failed. Check your credentials.');
    } finally {
      setIsConnecting(false);
    }
  };

  // Disconnect Wyze
  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Wyze account? This will remove all plug assignments.')) return;

    try {
      const configRef = doc(db, 'users', currentUser.uid, 'tvPower', 'config');
      await setDoc(configRef, {
        isConnected: false,
        plugs: [],
        assignments: {},
        wyzeEmail: '',
        connectedAt: null
      }, { merge: true });

      setIsConnected(false);
      setPlugs([]);
      setAssignments({});
      setWyzeEmail('');
      setWyzePassword('');
      setWyzeKeyId('');
      setWyzeApiKey('');
    } catch (error) {
      console.error('Error disconnecting:', error);
    }
  };

  // Step 2: Discover plugs
  const handleDiscoverPlugs = async () => {
    setIsDiscovering(true);
    setDiscoverError('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_CLOUD_FUNCTIONS_URL}/wyzeDiscoverPlugs`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: currentUser.uid })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to discover plugs.');
      }

      const discoveredPlugs = result.plugs || [];

      // Save to Firestore
      const configRef = doc(db, 'users', currentUser.uid, 'tvPower', 'config');
      await updateDoc(configRef, { plugs: discoveredPlugs });

      setPlugs(discoveredPlugs);
    } catch (error) {
      console.error('Discover error:', error);
      setDiscoverError(error.message || 'Failed to discover plugs.');
    } finally {
      setIsDiscovering(false);
    }
  };

  // Toggle plug on/off via Cloud Function
  const handleTogglePlug = async (plugMac, currentlyOn) => {
    setTogglingMac(plugMac);

    try {
      const action = currentlyOn ? 'off' : 'on';

      const response = await fetch(
        `${process.env.REACT_APP_CLOUD_FUNCTIONS_URL}/wyzeTogglePlug`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: currentUser.uid,
            mac: plugMac,
            action: action
          })
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Failed to turn ${action} plug.`);
      }

      // Update local state immediately
      const updatedStates = {
        ...plugStates,
        [plugMac]: {
          isOn: action === 'on',
          lastChecked: Date.now()
        }
      };
      setPlugStates(updatedStates);

      // Write to Firestore so Screens page badge picks it up
      const configRef = doc(db, 'users', currentUser.uid, 'tvPower', 'config');
      await updateDoc(configRef, { plugStates: updatedStates });

    } catch (error) {
      console.error('Toggle error:', error);
      alert(error.message || 'Failed to toggle plug.');
    } finally {
      setTogglingMac(null);
    }
  };

  // Step 3: Assign plug to screen
  const handleAssign = (plugMac, screenId) => {
    setAssignments(prev => {
      const updated = { ...prev };
      if (screenId === '') {
        delete updated[plugMac];
      } else {
        // Remove any existing assignment for this screen (one plug per screen)
        Object.keys(updated).forEach(mac => {
          if (updated[mac] === screenId) delete updated[mac];
        });
        updated[plugMac] = screenId;
      }
      return updated;
    });
    setSaveSuccess(false);
  };

  // Save assignments
  const handleSaveAssignments = async () => {
    setIsSaving(true);
    setSaveSuccess(false);

    try {
      const configRef = doc(db, 'users', currentUser.uid, 'tvPower', 'config');
      await updateDoc(configRef, { assignments });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving assignments:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Remove a single plug from the list
  const handleRemovePlug = async (mac) => {
    const updated = plugs.filter(p => p.mac !== mac);
    const updatedAssignments = { ...assignments };
    delete updatedAssignments[mac];

    try {
      const configRef = doc(db, 'users', currentUser.uid, 'tvPower', 'config');
      await updateDoc(configRef, { plugs: updated, assignments: updatedAssignments });
      setPlugs(updated);
      setAssignments(updatedAssignments);
    } catch (error) {
      console.error('Error removing plug:', error);
    }
  };

  // Helper: get screen name for a plug
  const getAssignedScreenName = (plugMac) => {
    const screenId = assignments[plugMac];
    if (!screenId) return null;
    const screen = screens.find(s => s.id === screenId);
    return screen ? screen.screenName : 'Unknown Screen';
  };

  // Helper: check if screen is already assigned to a plug
  const isScreenAssigned = (screenId) => {
    return Object.values(assignments).includes(screenId);
  };

  if (loading) {
    return <div className="loading">Loading Smart Power settings...</div>;
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div className="page-header">
        <h1 className="page-title">Smart Power</h1>
      </div>

      <p style={{ color: '#666', marginBottom: '32px', fontSize: '14px' }}>
       Control your smart plugs. Connect your Wyze account, discover plugs on your network, then assign each plug to a device.
      </p>

      {/* ─── Section 1: Wyze Connection ─── */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {isConnected ? <Wifi size={20} color="#10b981" /> : <WifiOff size={20} color="#999" />}
            <h2 style={{ margin: 0, fontSize: '18px' }}>Wyze Connection</h2>
          </div>
          {isConnected && (
            <span style={{
              backgroundColor: '#d1fae5',
              color: '#059669',
              fontSize: '12px',
              fontWeight: 500,
              padding: '4px 12px',
              borderRadius: '12px'
            }}>
              Connected
            </span>
          )}
        </div>

        {isConnected ? (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px'
            }}>
              <div>
                <div style={{ fontSize: '14px', color: '#333', fontWeight: 500 }}>{wyzeEmail}</div>
                <div style={{ fontSize: '12px', color: '#999' }}>Wyze account linked</div>
              </div>
              <button
                onClick={handleDisconnect}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  border: '1px solid #fca5a5',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#dc2626',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                <Unlink size={14} />
                Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="form-group">
              <label className="form-label">Wyze Email</label>
              <input
                type="email"
                className="form-input"
                placeholder="your@email.com"
                value={wyzeEmail}
                onChange={e => setWyzeEmail(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Wyze Password</label>
              <input
                type="password"
                className="form-input"
                placeholder="••••••••"
                value={wyzePassword}
                onChange={e => setWyzePassword(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">API Key ID</label>
              <input
                type="text"
                className="form-input"
                placeholder="Your Wyze API Key ID"
                value={wyzeKeyId}
                onChange={e => setWyzeKeyId(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">API Key</label>
              <input
                type="text"
                className="form-input"
                placeholder="Your Wyze API Key"
                value={wyzeApiKey}
                onChange={e => setWyzeApiKey(e.target.value)}
              />
            </div>

            {connectionError && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#dc2626',
                backgroundColor: '#fef2f2',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                <AlertCircle size={16} />
                {connectionError}
              </div>
            )}

            <button
              className="btn btn-primary"
              onClick={handleConnect}
              disabled={isConnecting}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Link size={16} />
              {isConnecting ? 'Connecting...' : 'Connect Wyze Account'}
            </button>
          </div>
        )}
      </div>

      {/* ─── Section 2: Discover Plugs ─── */}
      {isConnected && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Plug size={20} color="#666" />
              <h2 style={{ margin: 0, fontSize: '18px' }}>Smart Plugs</h2>
            </div>
            <button
              onClick={handleDiscoverPlugs}
              disabled={isDiscovering}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                backgroundColor: 'white',
                color: '#333',
                fontSize: '13px',
                cursor: isDiscovering ? 'not-allowed' : 'pointer',
                opacity: isDiscovering ? 0.6 : 1
              }}
            >
              <RefreshCw size={14} className={isDiscovering ? 'spinning' : ''} />
              {isDiscovering ? 'Scanning...' : 'Discover Plugs'}
            </button>
          </div>

          {discoverError && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#dc2626',
              backgroundColor: '#fef2f2',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              <AlertCircle size={16} />
              {discoverError}
            </div>
          )}

          {plugs.length > 0 ? (
            <div>
              {plugs.map((plug) => {
                const isOn = plugStates[plug.mac]?.isOn || false;
                const isToggling = togglingMac === plug.mac;

                return (
                  <div
                    key={plug.mac}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      marginBottom: '8px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                      <Plug size={18} color={isOn ? '#2563eb' : '#999'} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>
                          {plug.nickname || plug.mac}
                        </div>
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          {plug.model || 'Smart Plug'} · {plug.mac}
                          {getAssignedScreenName(plug.mac) && (
                            <span style={{ color: '#2563eb' }}> · {getAssignedScreenName(plug.mac)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                      {/* Toggle Switch */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 500, color: isOn ? '#2563eb' : '#999' }}>
                          {isOn ? 'On' : 'Off'}
                        </span>
                        <button
                          onClick={() => handleTogglePlug(plug.mac, isOn)}
                          disabled={isToggling}
                          style={{
                            position: 'relative',
                            width: '44px',
                            height: '24px',
                            borderRadius: '12px',
                            border: 'none',
                            backgroundColor: isOn ? '#2563eb' : '#d1d5db',
                            cursor: isToggling ? 'not-allowed' : 'pointer',
                            transition: 'background-color 0.2s ease',
                            opacity: isToggling ? 0.6 : 1,
                            padding: 0,
                            flexShrink: 0
                          }}
                          title={isOn ? 'Turn off' : 'Turn on'}
                        >
                          <div style={{
                            position: 'absolute',
                            top: '2px',
                            left: isOn ? '22px' : '2px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            backgroundColor: 'white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            transition: 'left 0.2s ease'
                          }} />
                        </button>
                      </div>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 500,
                        padding: '3px 8px',
                        borderRadius: '10px',
                        backgroundColor: plug.isOnline ? '#d1fae5' : '#f3f4f6',
                        color: plug.isOnline ? '#059669' : '#999'
                      }}>
                        {plug.isOnline ? 'Online' : 'Offline'}
                      </span>
                      <button
                        onClick={() => handleRemovePlug(plug.mac)}
                        style={{
                          padding: '6px',
                          border: 'none',
                          backgroundColor: 'transparent',
                          color: '#999',
                          cursor: 'pointer'
                        }}
                        title="Remove plug"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '32px',
              color: '#999',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px dashed #e5e7eb'
            }}>
              <Plug size={32} color="#ddd" style={{ marginBottom: '12px' }} />
              <p style={{ margin: 0, fontSize: '14px' }}>
                No plugs found. Click "Discover Plugs" to scan your Wyze account.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ─── Section 3: Assign Plugs to Screens ─── */}
      {isConnected && plugs.length > 0 && (
        <div className="card" style={{ marginBottom: '40px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <Monitor size={20} color="#666" />
            <h2 style={{ margin: 0, fontSize: '18px' }}>Assign Plugs to Devices</h2>
          </div>

          <p style={{ color: '#999', fontSize: '13px', marginBottom: '20px' }}>
            Map each smart plug to the device it powers. One plug per device.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
            {plugs.map((plug) => {
              const assignedScreenId = assignments[plug.mac] || '';

              return (
                <div
                  key={plug.mac}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '14px 16px',
                    backgroundColor: assignedScreenId ? '#f0fdf4' : '#f9fafb',
                    borderRadius: '8px',
                    border: assignedScreenId ? '1px solid #bbf7d0' : '1px solid #e5e7eb',
                    flexWrap: 'wrap'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '180px', flex: '1 1 180px' }}>
                    <Plug size={16} color="#666" />
                    <span style={{ fontSize: '14px', fontWeight: 500 }}>
                      {plug.nickname || plug.mac}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#999', fontSize: '13px', flex: '0 0 auto' }}>
                    →
                  </div>

                  <select
                    value={assignedScreenId}
                    onChange={e => handleAssign(plug.mac, e.target.value)}
                    style={{
                      flex: '1 1 200px',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      minWidth: '180px'
                    }}
                  >
                    <option value="">-- No Screen --</option>
                    {screens.map(screen => (
                      <option
                        key={screen.id}
                        value={screen.id}
                        disabled={isScreenAssigned(screen.id) && assignments[plug.mac] !== screen.id}
                      >
                        {screen.screenName}
                        {isScreenAssigned(screen.id) && assignments[plug.mac] !== screen.id ? ' (assigned)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              className="btn btn-primary"
              onClick={handleSaveAssignments}
              disabled={isSaving}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 18px', fontSize: '13px', fontWeight: 500 }}
            >
              {isSaving ? 'Saving...' : 'Save Assignments'}
            </button>

            {saveSuccess && (
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#059669',
                fontSize: '14px',
                fontWeight: 500
              }}>
                <Check size={16} />
                Saved
              </span>
            )}
          </div>
        </div>
      )}

      {/* Empty state when not connected */}
      {!isConnected && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#999',
          fontSize: '14px'
        }}>
          Connect your Wyze account above to get started with Smart Power.
        </div>
      )}
    </div>
  );
}

export default TvPower;